<template>
  <RequiresPermission
    permission="view_platform_overview"
    fallback-route-name="merchantIndex"
  >
    <div
      class="mb-4 flex w-full shrink-0 items-center justify-between gap-4"
    >
      <div
        class="flex w-full flex-wrap justify-center gap-4 md:mx-auto"
      >
        <ButtonExport
          :is-loading="isExporting"
          @export="onExport"
        />
        <div
          class="flex items-center justify-end md:ml-auto"
        >
          <label
            class="mr-2 text-xs text-background-fg/87"
            for="currency"
            >Currency:</label
          >
          <InputSelect
            v-model="selectedCurrency"
            :options="merchantCurrencyCodeOptions"
          />
        </div>
        <div class="flex shrink-0 items-center">
          <span class="mr-2 text-xs text-background-fg/87">
            Overview for month:
          </span>
          <ReportDateSelect
            v-model="selectedMonthStart"
            type="month"
            disable-current-month
          />
        </div>
      </div>
    </div>
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <BaseCard class="relative">
        <QueryLoading
          v-if="isEngagementOverviewLoading"
          class="absolute inset-0"
        />
        <QueryError
          v-else-if="isEngagementOverviewError"
          class="min-h-72"
          :show-retry="true"
          @retry="refetchEngagementOverview"
        >
          Failed to retrieve engagement overview
        </QueryError>
        <template v-else>
          <div class="mb-3 flex items-center justify-start">
            <h3
              class="text-xl font-semibold text-background-fg/70"
            >
              Transaction Totals ({{
                DateFns.format(
                  DateFns.parse(
                    selectedMonthStart,
                    "yyyy-MM-dd",
                    new Date()
                  ),
                  "MMM yyyy"
                )
              }})
            </h3>
            <div
              :class="[
                'ml-auto flex items-center justify-center text-lg font-semibold text-primary',
              ]"
            >
              {{
                engagementOverViewRes
                  ? formatToCurrencyString(
                      engagementOverViewRes.transactionScoreMean,
                      selectedCurrency
                    )
                  : ""
              }}
            </div>
          </div>
          <div>
            <BarChart
              :data="transactionChartData"
              :options="chartOptions"
            />
          </div>
        </template>
      </BaseCard>

      <BaseCard>
        <div class="mb-3 flex items-center justify-start">
          <h3
            class="text-xl font-semibold text-background-fg/70"
          >
            Engagement Scores
          </h3>
          <div
            :class="[
              'ml-auto flex size-12 shrink-0 items-center justify-center rounded-full border-2 text-lg font-semibold',
              `border-info text-info`,
            ]"
          >
            {{
              engagementOverViewRes?.engagementScoreMean.toFixed(
                1
              )
            }}
          </div>
        </div>
        <div>
          <BarChart
            :data="engagementChartData"
            :options="chartOptions"
          />
        </div>
      </BaseCard>

      <BaseCard>
        <div class="mb-3 flex items-center justify-start">
          <h3
            class="text-xl font-semibold text-background-fg/70"
          >
            Most Transacting
          </h3>
        </div>
        <div>
          <BaseTable
            :rows="mostTransacting?.items || []"
            :columns="topScoringTableColumns"
          >
            <template #cell-merchantName="{ row }">
              <BaseTableChevronLink
                :to="{
                  name: 'merchantDetail',
                  params: {
                    tenantId: row.id,
                  },
                }"
              >
                <p>
                  {{ row.merchantName }}
                </p>
                <p
                  class="text-xs font-medium text-background-fg/50"
                >
                  {{ row.tpReference }}
                </p>
              </BaseTableChevronLink>
            </template>
            <template #cell-totalTransactions="{ value }">
              <template v-if="value !== null">
                <div
                  class="ml-2 flex shrink-0 items-center justify-center font-semibold text-primary"
                >
                  {{ value }}
                </div>
              </template>
            </template>

            <template #cell-engagementScore="{ value }">
              <template v-if="value !== null">
                <div
                  class="ml-2 flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-info font-semibold text-info"
                >
                  {{ value.toFixed(1) }}
                </div>
              </template>
            </template>
          </BaseTable>
        </div>
      </BaseCard>

      <BaseCard>
        <div class="mb-3 flex items-center justify-start">
          <h3
            class="text-xl font-semibold text-background-fg/70"
          >
            Most Engaged
          </h3>
        </div>
        <div>
          <BaseTable
            :rows="mostEngaged?.items || []"
            :columns="topScoringTableColumns"
          >
            <template #cell-merchantName="{ row }">
              <BaseTableChevronLink
                :to="{
                  name: 'merchantDetail',
                  params: {
                    tenantId: row.id,
                  },
                }"
              >
                <p>
                  {{ row.merchantName }}
                </p>
                <p
                  class="text-xs font-medium text-background-fg/50"
                >
                  {{ row.tpReference }}
                </p>
              </BaseTableChevronLink>
            </template>
            <template #cell-totalTransactions="{ value }">
              <template v-if="value !== null">
                <div
                  class="ml-2 flex shrink-0 items-center justify-center font-semibold text-primary"
                >
                  {{ value }}
                </div>
              </template>
            </template>

            <template #cell-engagementScore="{ value }">
              <template v-if="value !== null">
                <div
                  class="ml-2 flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-info font-semibold text-info"
                >
                  {{ value.toFixed(1) }}
                </div>
              </template>
            </template>
          </BaseTable>
        </div>
      </BaseCard>
    </div>
  </RequiresPermission>
</template>

<script setup lang="ts">
import { useRequiredPermissions } from "@/composables/useRequiredPermissions"
import { Chart as ChartJS, registerables } from "chart.js"
import { Bar as BarChart } from "vue-chartjs"

import type { ChartData, ChartOptions } from "chart.js"
import type { TableColumn } from "@/components/BaseTable.vue"
import type { TopScoringMerchant } from "@/services/engagement/listTopScoringMerchants"
import type { MerchantCurrencyCode } from "@/utils/currency"

ChartJS.register(...registerables)

const { hasRequiredPermissions } = useRequiredPermissions(
  "view_platform_overview"
)
const { defaultMerchantCurrency } = useOrganizationConfig()
const selectedCurrencyOverride =
  ref<MerchantCurrencyCode | null>(null)
const selectedCurrency = computed<MerchantCurrencyCode>({
  get: () =>
    selectedCurrencyOverride.value ??
    defaultMerchantCurrency.value,
  set: (newValue) => {
    selectedCurrencyOverride.value = newValue
  },
})
const selectedMonthStart = ref(
  DateFns.format(
    DateFns.setDate(DateFns.addMonths(new Date(), -1), 1),
    "yyyy-MM-dd"
  )
)

const {
  data: engagementOverViewRes,
  isLoading: isEngagementOverviewLoading,
  isError: isEngagementOverviewError,
  refetch: refetchEngagementOverview,
} = useEngagementOverview(
  computed(() => ({
    forMonthStartingOn: selectedMonthStart.value,
    currency: selectedCurrency.value,
  }))
)

const chartOptions: ChartOptions<"bar"> = {
  plugins: {
    tooltip: {
      enabled: false,
      mode: "nearest",
      intersect: false,
      axis: "x",
    },
    legend: {
      display: false,
    },
  },

  scales: {
    x: {
      grid: {
        display: false,
        drawTicks: false,
      },
      ticks: {
        font: {
          size: 12,
        },
      },
    },
    y: {
      grid: {
        display: false,
        drawTicks: false,
      },
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        font: {
          size: 12,
        },
      },
      stacked: false,
    },
  },
}

const colorTransaction = computed(
  () => `rgb(${useCssVar("--color-primary").value} / 0.6)`
)
const transactionChartData = computed<
  ChartData<"bar", { x: string; y: number }[]>
>(() => ({
  datasets: [
    {
      backgroundColor: colorTransaction.value,
      borderRadius: 4,
      data: engagementOverViewRes.value
        ? engagementOverViewRes.value.transactionScoreDistribution.map(
            ({ label, count }) => ({
              x: String(label),
              y: count,
            })
          )
        : [],
    },
  ],
}))

const colorEngagement = computed(
  () => `rgb(${useCssVar("--color-info").value} / 0.6)`
)
const engagementChartData = computed<
  ChartData<"bar", { x: string; y: number }[]>
>(() => ({
  datasets: [
    {
      backgroundColor: colorEngagement.value,
      borderRadius: 4,
      data: engagementOverViewRes.value
        ? engagementOverViewRes.value.engagementScoreDistribution.map(
            ({ label, count }) => ({
              x: String(label),
              y: count,
            })
          )
        : [],
    },
  ],
}))

const { data: mostTransacting } = useTopScoringMerchants(
  computed(() => ({
    forMonthStartingOn: selectedMonthStart.value,
    ordering: "-totalTransactions",
    limit: 10,
    offset: 0,
    currency: selectedCurrency.value,
  })),
  hasRequiredPermissions
)

const { data: mostEngaged } = useTopScoringMerchants(
  computed(() => ({
    forMonthStartingOn: selectedMonthStart.value,
    ordering: "-engagementScore",
    limit: 10,
    offset: 0,
    currency: selectedCurrency.value,
  })),
  hasRequiredPermissions
)

type ColumnId =
  | "merchantName"
  | "totalTransactions"
  | "engagementScore"
const topScoringTableColumns = computed<
  Array<TableColumn<TopScoringMerchant, ColumnId>>
>(() => [
  {
    id: "merchantName",
    header: "Merchant Name",
    getCellValue: (row) => row.merchantName,
  },
  {
    id: "totalTransactions",
    header: `Transacted (${DateFns.format(
      DateFns.parse(
        selectedMonthStart.value,
        "yyyy-MM-dd",
        new Date()
      ),
      "MMM yyyy"
    )})`,
    getCellValue: (row) =>
      formatToCurrencyString(
        row.totalTransactions ?? 0,
        row.currency
      ),
  },
  {
    id: "engagementScore",
    header: "EScore",
    getCellValue: (row) => row.engagementScore,
  },
])

const isExporting = ref(false)
const onExport = async (format?: FileExportFormat) => {
  if (isExporting.value) return
  isExporting.value = true
  try {
    await exportEngagementReport(
      {
        forMonthStartingOn: selectedMonthStart.value,
      },
      format
    )
  } finally {
    isExporting.value = false
  }
}
</script>
